import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { api, API_BASE } from '../../api/client';

interface Category { id: number; title: string; description: string; }
interface Doc {
  id: string;
  documentationName: string;
  description: string;
  language: string;
  documentDate: string;
  createdAt: string;
}

const EMPTY_DOC = { documentationName: '', description: '', language: 'ru', documentDate: '' };

export default function AdminDocumentsPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  // Форма документа
  const [docForm, setDocForm] = useState(EMPTY_DOC);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docCategoryId, setDocCategoryId] = useState<string>('');
  const [savingDoc, setSavingDoc] = useState(false);

  // Форма категории
  const [catForm, setCatForm] = useState({ title: '', description: '' });
  const [savingCat, setSavingCat] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);

  const loadCategories = () =>
    api.get('/categories/get-all').then(r => setCategories(r.data)).catch(() => {});

  const loadDocs = (catId: number | null) => {
    const url = catId
      ? `${API_BASE}/documentations/get-by-category/${catId}`
      : `${API_BASE}/documentations/get-all`;
    axios.get(url).then(r => setDocs(r.data)).catch(() => setDocs([]));
  };

  const handleDocFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocFile(e.target.files?.[0] || null);
    e.target.value = '';
  };

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { loadDocs(selectedCategory); }, [selectedCategory]);

  const createDoc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!docCategoryId) return alert('Выберите категорию');
    if (!docFile) return alert('Прикрепите файл документа');
    setSavingDoc(true);
    try {
      const fd = new FormData();
      fd.append('documentation', new Blob([JSON.stringify(docForm)], { type: 'application/json' }));
      fd.append('file', docFile);
      await api.post(`/documentations/create?categoryId=${docCategoryId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocForm(EMPTY_DOC);
      setDocFile(null);
      setDocCategoryId('');
      loadDocs(selectedCategory);
    } finally {
      setSavingDoc(false);
    }
  };

  const removeDoc = async (id: string) => {
    if (!window.confirm('Удалить документ?')) return;
    await api.delete(`/documentations/delete-by-id/${id}`);
    loadDocs(selectedCategory);
  };

  const createCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingCat(true);
    try {
      await api.post('/categories/create', catForm);
      setCatForm({ title: '', description: '' });
      setShowCatForm(false);
      await loadCategories();
    } finally {
      setSavingCat(false);
    }
  };

  const handleDownload = (id: string, name: string) => {
    axios.get(`${API_BASE}/documentations/download/${id}`, { responseType: 'blob' })
      .then(r => {
        const contentType = r.headers['content-type'] || 'application/octet-stream';
        const disposition = r.headers['content-disposition'] || '';
        const match = disposition.match(/filename="?([^"]+)"?/);
        const filename = match ? match[1] : name;
        const blob = new Blob([r.data], { type: contentType as string });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  };

  return (
    <div style={s.page}>
      <h2 style={s.pageTitle}>Управление документами</h2>

      {/* Категории */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <h3 style={s.cardTitle}>Категории ({categories.length})</h3>
          <button style={s.btnSmall} onClick={() => setShowCatForm(p => !p)}>
            {showCatForm ? 'Скрыть' : '➕ Добавить категорию'}
          </button>
        </div>

        {showCatForm && (
          <form onSubmit={createCategory} style={{ ...s.form, borderBottom: '1px solid #f0f4fb' }}>
            <div style={s.row2}>
              <input
                style={s.input} placeholder="Название категории *"
                value={catForm.title}
                onChange={e => setCatForm({ ...catForm, title: e.target.value })}
                required
              />
              <input
                style={s.input} placeholder="Описание"
                value={catForm.description}
                onChange={e => setCatForm({ ...catForm, description: e.target.value })}
              />
            </div>
            <button style={s.btnPrimary} type="submit" disabled={savingCat}>
              {savingCat ? 'Сохранение...' : '➕ Создать'}
            </button>
          </form>
        )}

        <div style={s.catList}>
          {categories.map(c => (
            <button
              key={c.id}
              style={{ ...s.catBtn, ...(selectedCategory === c.id ? s.catBtnActive : {}) }}
              onClick={() => setSelectedCategory(selectedCategory === c.id ? null : c.id)}
            >
              {c.title}
            </button>
          ))}
        </div>
      </div>

      {/* Загрузить документ */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>➕ Загрузить документ</h3>
        <form onSubmit={createDoc} style={s.form}>
          <div style={s.row2}>
            <input
              style={s.input} placeholder="Название документа *"
              value={docForm.documentationName}
              onChange={e => setDocForm({ ...docForm, documentationName: e.target.value })}
              required
            />
            <select
              style={s.input}
              value={docCategoryId}
              onChange={e => setDocCategoryId(e.target.value)}
              required
            >
              <option value="">— Выберите категорию —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
          <textarea
            style={{ ...s.input, resize: 'vertical' }}
            placeholder="Описание (необязательно)"
            rows={2}
            value={docForm.description}
            onChange={e => setDocForm({ ...docForm, description: e.target.value })}
          />
          <div style={s.row2}>
            <select
              style={s.input}
              value={docForm.language}
              onChange={e => setDocForm({ ...docForm, language: e.target.value })}
            >
              <option value="ru">Русский</option>
              <option value="ky">Кыргызский</option>
              <option value="en">English</option>
            </select>
            <input
              style={s.input} type="date"
              value={docForm.documentDate}
              onChange={e => setDocForm({ ...docForm, documentDate: e.target.value })}
              required
            />
          </div>
          <div>
            <label style={s.fileLabel}>
              📎 Файл документа *
              <input
                type="file" style={{ display: 'none' }}
                onChange={handleDocFileChange}
              />
            </label>
            {docFile && <span style={s.fileName}>{docFile.name}</span>}
          </div>
          <button style={s.btnPrimary} type="submit" disabled={savingDoc}>
            {savingDoc ? 'Загрузка...' : '⬆ Загрузить'}
          </button>
        </form>
      </div>

      {/* Список документов */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>
          {selectedCategory
            ? `Документы: ${categories.find(c => c.id === selectedCategory)?.title}`
            : `Все документы (${docs.length})`}
        </h3>
        {docs.length === 0 ? (
          <p style={s.empty}>Документов нет</p>
        ) : (
          docs.map(doc => (
            <div key={doc.id} style={s.docRow}>
              <span style={{ fontSize: '1.5rem' }}>📄</span>
              <div style={s.docInfo}>
                <div style={s.docName}>{doc.documentationName}</div>
                {doc.description && <div style={s.docDesc}>{doc.description}</div>}
                <div style={s.docMeta}>
                  {doc.language && <span style={s.badge}>{doc.language.toUpperCase()}</span>}
                  {doc.documentDate && (
                    <span style={s.metaDate}>
                      {new Date(doc.documentDate).toLocaleDateString('ru-RU')}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => handleDownload(doc.id, doc.documentationName)} style={s.btnDownload}>⬇</button>
              <button onClick={() => removeDoc(doc.id)} style={s.btnDanger}>🗑</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { padding: 'clamp(16px, 4vw, 32px)', maxWidth: 960, margin: '0 auto' },
  pageTitle: { color: '#1a3a6b', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', marginBottom: 'clamp(16px, 3vw, 24px)' },
  card: { background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 16, overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f0f4fb', flexWrap: 'wrap', gap: 10 },
  cardTitle: { color: '#1a3a6b', fontSize: 'clamp(0.9rem, 2vw, 1rem)', margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px 20px' },
  row2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 12 },
  input: { padding: '10px 14px', borderRadius: 8, border: '1px solid #dde3ef', fontSize: '0.95rem', outline: 'none', width: '100%' },
  fileLabel: { display: 'inline-block', padding: '8px 16px', background: '#f0f4fb', border: '1px dashed #1a3a6b', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', color: '#1a3a6b', fontWeight: 600 },
  fileName: { marginLeft: 10, fontSize: '0.85rem', color: '#555' },
  btnPrimary: { padding: '10px 24px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
  btnSmall: { padding: '7px 14px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  catList: { display: 'flex', gap: 10, flexWrap: 'wrap', padding: '14px 20px' },
  catBtn: { padding: '7px 16px', borderRadius: 20, border: '2px solid #1a3a6b', background: 'transparent', color: '#1a3a6b', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  catBtnActive: { background: '#1a3a6b', color: '#fff' },
  empty: { padding: '16px 20px', color: '#888', fontSize: '0.9rem' },
  docRow: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderTop: '1px solid #f0f4fb', flexWrap: 'wrap' },
  docInfo: { flex: 1, minWidth: 200 },
  docName: { fontWeight: 700, color: '#1a2a4a', fontSize: '0.95rem', marginBottom: 2, wordBreak: 'break-word' },
  docDesc: { fontSize: '0.85rem', color: '#666', marginBottom: 4, wordBreak: 'break-word' },
  docMeta: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  badge: { background: '#e8f0fe', color: '#1a3a6b', borderRadius: 4, padding: '2px 8px', fontSize: '0.78rem', fontWeight: 700 },
  metaDate: { fontSize: '0.82rem', color: '#999' },
  btnDownload: { padding: '6px 10px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0 },
  btnDanger: { padding: '6px 10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0 },
};
